import { readOperationalOrigin, type OperationalOrigin } from '../components/ops/exceptionLoop';

export type IssueOriginKind = 'manual' | 'operational' | 'recorded' | 'unrecorded';

export interface IssueOriginContextLine {
  label: string;
  value: string;
}

export interface IssueOriginView {
  kind: IssueOriginKind;
  label: string;
  context: IssueOriginContextLine[];
  navigation: { href: string } | null;
  unavailableReason: string | null;
  /** Origem não é causa, não muda status e não cria tratamento. */
  isCause: false;
  changesStatus: false;
  createsAction: false;
  createsOpportunity: false;
}

export interface IssueOriginInput {
  organizationId?: string | null;
  source?: string | null;
  projectId?: string | null;
  metadata?: unknown;
  /** false quando o objeto apontado não existe mais. null = não verificado. */
  targetExists?: boolean | null;
}

const KNOWN_SOURCE_LABELS: Record<string, string> = {
  manual: 'Manual',
  lst_tpl: 'Rotina de liderança',
};

function recordedLabel(source: string): string {
  if (KNOWN_SOURCE_LABELS[source]) return KNOWN_SOURCE_LABELS[source];
  if (source.startsWith('painel:')) return 'Painel operacional';
  return source;
}

function contextFrom(origin: OperationalOrigin): IssueOriginContextLine[] {
  const lines: IssueOriginContextLine[] = [];
  if (origin.signal_label) lines.push({ label: 'Sinal', value: origin.signal_label });
  if (origin.stage_name) lines.push({ label: 'Etapa', value: origin.stage_name });
  if (origin.wip != null) lines.push({ label: 'Valor observado', value: String(origin.wip) });
  if (origin.detected_at) lines.push({ label: 'Detectado em', value: origin.detected_at });
  if (origin.avg_minutes != null) lines.push({ label: 'Tempo parado', value: `${origin.avg_minutes} min` });
  if (origin.department_name) lines.push({ label: 'Departamento', value: origin.department_name });
  return lines.slice(0, 4);
}

function hasOperationalRef(origin: OperationalOrigin): boolean {
  return Boolean(origin.execution_id || origin.task_id || origin.action_id || origin.workflow_id);
}

/**
 * Leitura única da origem.
 * `projectId` é contexto de trabalho e não vira origem.
 * A coluna `source` é o rótulo já gravado. O JSON operacional, quando existe, prevalece.
 */
export function getIssueOrigin(input: IssueOriginInput): IssueOriginView {
  const base = {
    isCause: false as const,
    changesStatus: false as const,
    createsAction: false as const,
    createsOpportunity: false as const,
  };
  void input.projectId;

  const operational = readOperationalOrigin(input.metadata);
  if (operational) {
    const otherOrg = Boolean(
      operational.organization_id
      && input.organizationId
      && operational.organization_id !== input.organizationId,
    );
    const missing = input.targetExists === false && hasOperationalRef(operational);
    let navigation: { href: string } | null = operational.return_path ? { href: operational.return_path } : null;
    let unavailableReason: string | null = null;
    if (otherOrg) {
      navigation = null;
      unavailableReason = 'A referência pertence a outra organização.';
    } else if (missing) {
      navigation = null;
      unavailableReason = 'O objeto de origem não está mais disponível.';
    } else if (!operational.return_path) {
      navigation = null;
    }
    return {
      ...base,
      kind: 'operational',
      label: operational.label,
      context: contextFrom(operational),
      navigation,
      unavailableReason,
    };
  }

  const source = input.source?.trim() ?? '';
  if (source === 'manual') {
    return { ...base, kind: 'manual', label: 'Manual', context: [], navigation: null, unavailableReason: null };
  }
  if (source) {
    return {
      ...base,
      kind: 'recorded',
      label: recordedLabel(source),
      context: [],
      navigation: null,
      unavailableReason: null,
    };
  }

  return {
    ...base,
    kind: 'unrecorded',
    label: 'Origem não registrada',
    context: [],
    navigation: null,
    unavailableReason: null,
  };
}

export function getIssueOriginNavigation(input: IssueOriginInput): { href: string } | null {
  return getIssueOrigin(input).navigation;
}

const PRESENTATION_LABELS: Record<string, string> = {
  andon: 'Andon',
  cliente: 'Cliente',
  inspeção_final: 'Inspeção final',
  inspecao_final: 'Inspeção final',
  logística_interna: 'Logística interna',
  logistica_interna: 'Logística interna',
  gestão_projetos: 'Gestão de projetos',
  gestao_projetos: 'Gestão de projetos',
  pintura: 'Pintura',
  produção: 'Produção',
  producao: 'Produção',
  inspeção_visual: 'Inspeção visual',
  inspecao_visual: 'Inspeção visual',
  workflow: 'Workflow',
};

/** Rótulo de tela. Não altera o valor gravado em `issues.source`. */
export function presentIssueOrigin(view: Pick<IssueOriginView, 'kind' | 'label'>): string {
  if (view.kind !== 'recorded') return view.label;
  if (PRESENTATION_LABELS[view.label]) return PRESENTATION_LABELS[view.label];
  if (view.label !== view.label.toLowerCase() || view.label.includes(' ')) return view.label;
  const words = view.label.split('_').filter(Boolean);
  if (words.length === 0) return view.label;
  return words.map((word) => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');
}
